import React, { useCallback, useEffect, useRef } from "react";
import { Box, Button, Text, HStack } from "@chakra-ui/react";
import { useModelStore, usePlacedModelsStore, useHistoryStore, useUserInterfaceStore } from "../../store";

// Step sizes (tunable) — smaller steps + faster ticks = smooth continuous feel
const POSITION_STEP = 1.5;
const HEIGHT_STEP = 0.75;
const ROTATION_STEP = Math.PI / 24; // ~7.5 degrees
const SCALE_GROW = 1.02;
const SCALE_SHRINK = 0.98;
const TICK_INTERVAL = 35; // ms (~28 ticks/sec)

function useHoldButton(onTick) {
  const intervalRef = useRef(null);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    stop(); // clear any lingering interval
    useHistoryStore.getState().push(usePlacedModelsStore.getState().placedModels);
    onTick();
    intervalRef.current = setInterval(onTick, TICK_INTERVAL);
  }, [onTick, stop]);

  return {
    onPointerDown: start,
    onPointerUp: stop,
    onPointerLeave: stop,
    onPointerCancel: stop,
    onContextMenu: (e) => { e.preventDefault(); stop(); },
  };
}

function TransformButton({ label, onTick, ...props }) {
  const holdProps = useHoldButton(onTick);
  return (
    <Button
      size="sm"
      variant="outline"
      borderColor="gray.500"
      color="white"
      _hover={{ bg: "gray.700" }}
      _active={{ bg: "gray.600" }}
      borderRadius="md"
      fontFamily="DM Mono"
      userSelect="none"
      touchAction="none"
      minW={0}
      {...holdProps}
      {...props}
    >
      {label}
    </Button>
  );
}

// Square button for the d-pad
function DpadButton({ label, onTick, ...props }) {
  const holdProps = useHoldButton(onTick);
  return (
    <Button
      variant="outline"
      borderColor="gray.500"
      color="white"
      _hover={{ bg: "gray.700" }}
      _active={{ bg: "gray.600" }}
      borderRadius="md"
      fontFamily="DM Mono"
      userSelect="none"
      touchAction="none"
      w="52px"
      h="52px"
      minW={0}
      p={0}
      fontSize="xl"
      {...holdProps}
      {...props}
    >
      {label}
    </Button>
  );
}

export default function MobileTransformPanel() {
  const selectedMeshes = useModelStore((s) => s.selectedMeshes);
  const selectedMesh = selectedMeshes?.[0];
  const sourceId = selectedMesh?.userData?.sourceId;
  const model = usePlacedModelsStore((s) => s.getModelById(sourceId));
  const updateTransform = usePlacedModelsStore((s) => s.updateTransform);
  const rotationAxes = useUserInterfaceStore((s) => s.rotationAxes);
  const setRotationAxes = useUserInterfaceStore((s) => s.setRotationAxes);

  const hasSelection = !!(model && sourceId);

  // Reset rotation axis to Y whenever a new object is selected
  useEffect(() => {
    if (!sourceId) return;
    setRotationAxes({ x: false, y: true, z: false });
  }, [sourceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const noop = () => () => {};

  const applyDelta = (fn) => {
    const current = usePlacedModelsStore.getState().getModelById(sourceId);
    if (!current) return;
    const transform = fn(current);
    updateTransform(sourceId, transform, true);
  };

  const moveX = hasSelection ? (dir) => () => applyDelta((m) => ({
    position: [m.position[0] + dir * POSITION_STEP, m.position[1], m.position[2]],
  })) : noop;
  const moveY = hasSelection ? (dir) => () => applyDelta((m) => ({
    position: [m.position[0], m.position[1] + dir * HEIGHT_STEP, m.position[2]],
  })) : noop;
  const moveZ = hasSelection ? (dir) => () => applyDelta((m) => ({
    position: [m.position[0], m.position[1], m.position[2] + dir * POSITION_STEP],
  })) : noop;
  const rotate = hasSelection ? (dir) => () => applyDelta((m) => ({
    rotation: [
      m.rotation[0] + (rotationAxes.x ? dir * ROTATION_STEP : 0),
      m.rotation[1] + (rotationAxes.y ? dir * ROTATION_STEP : 0),
      m.rotation[2] + (rotationAxes.z ? dir * ROTATION_STEP : 0),
    ],
  })) : noop;
  const scale = hasSelection ? (factor) => () => applyDelta((m) => ({
    scale: m.scale.map((s) => s * factor),
  })) : noop;

  return (
    <Box position="relative" p={3} bg="#141414" color="white" userSelect="none" sx={{ WebkitTouchCallout: "none" }}>
      {/* Grayed-out overlay when nothing is selected */}
      {!hasSelection && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="blackAlpha.700"
          zIndex={10}
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={4}
        >
          <Text
            color="gray.400"
            fontSize="md"
            fontFamily="DM Mono"
            textAlign="center"
          >
            Select an object to move
          </Text>
        </Box>
      )}

      <Box mb={2} px={1} opacity={hasSelection ? 1 : 0.4}>
        <Text fontSize="xs" color="gray.400" isTruncated>
          Selected: <Text as="span" color="white" fontWeight="bold">{model?.name || "\u2014"}</Text>
        </Text>
      </Box>

      {/* Main controls row: D-pad + Height + Rotate + Scale */}
      <HStack spacing={3} align="flex-start" opacity={hasSelection ? 1 : 0.4}>

        {/* XZ D-pad */}
        <Box>
          <Text fontSize="9px" fontWeight="bold" textTransform="uppercase" letterSpacing="wide" mb={1} color="gray.500" textAlign="center">
            Move
          </Text>
          <Box display="flex" flexDirection="column" alignItems="center" gap="2px">
            <DpadButton label="&#9650;" onTick={moveZ(-1)} />
            <HStack spacing="2px">
              <DpadButton label="&#9664;" onTick={moveX(-1)} />
              <DpadButton label="&#9654;" onTick={moveX(1)} />
            </HStack>
            <DpadButton label="&#9660;" onTick={moveZ(1)} />
          </Box>
        </Box>

        {/* Height / Rotate / Scale stacked */}
        <Box flex={1} display="flex" flexDirection="column" gap="6px">
          {/* Height (Y axis) */}
          <Box>
            <Text fontSize="9px" fontWeight="bold" textTransform="uppercase" letterSpacing="wide" mb={1} color="gray.500">
              Height
            </Text>
            <HStack spacing={1}>
              <TransformButton label="&#9660;" onTick={moveY(-1)} flex={1} h="36px" />
              <TransformButton label="&#9650;" onTick={moveY(1)} flex={1} h="36px" />
            </HStack>
          </Box>

          {/* Rotate */}
          <Box>
            <HStack justify="space-between" align="center" mb={1}>
              <Text fontSize="9px" fontWeight="bold" textTransform="uppercase" letterSpacing="wide" color="gray.500">
                Rotate
              </Text>
              <HStack spacing="3px">
                {["x", "y", "z"].map((axis) => (
                  <Button
                    key={axis}
                    size="xs"
                    variant={rotationAxes[axis] ? "solid" : "outline"}
                    bg={rotationAxes[axis] ? "white" : "transparent"}
                    color={rotationAxes[axis] ? "black" : "gray.400"}
                    borderColor="gray.600"
                    borderRadius="0"
                    fontFamily="DM Mono"
                    minW="22px"
                    h="18px"
                    p={0}
                    fontSize="9px"
                    onClick={() => setRotationAxes({ x: axis === "x", y: axis === "y", z: axis === "z" })}
                  >
                    {axis.toUpperCase()}
                  </Button>
                ))}
              </HStack>
            </HStack>
            <HStack spacing={1}>
              <TransformButton label="&#8634;" onTick={rotate(-1)} flex={1} h="36px" fontSize="lg" />
              <TransformButton label="&#8635;" onTick={rotate(1)} flex={1} h="36px" fontSize="lg" />
            </HStack>
          </Box>

          {/* Scale */}
          <Box>
            <Text fontSize="9px" fontWeight="bold" textTransform="uppercase" letterSpacing="wide" mb={1} color="gray.500">
              Scale
            </Text>
            <HStack spacing={1}>
              <TransformButton label="&#8722;" onTick={scale(SCALE_SHRINK)} flex={1} h="36px" fontSize="lg" />
              <TransformButton label="+" onTick={scale(SCALE_GROW)} flex={1} h="36px" fontSize="lg" />
            </HStack>
          </Box>
        </Box>

      </HStack>
    </Box>
  );
}
